import { forwardRef, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import styles from "./FormField.module.css";

interface CurrencyInputProps {
  label?: string;
  error?: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  className?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

/** Conta quantos caracteres "reais" (dígitos/vírgula) existem antes de `pos` numa string. */
function countRealCharsBefore(text: string, pos: number) {
  return text.slice(0, pos).replace(/[^\d,]/g, "").length;
}

/** Acha a posição em `text` logo após o N-ésimo caractere "real" (dígito/vírgula). */
function positionAfterRealChars(text: string, n: number) {
  if (n <= 0) return 0;
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (/[\d,]/.test(text[i])) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return text.length;
}

/** Formata dígitos brutos (com no máximo uma vírgula decimal) em "1.234,56", já com os pontos de milhar. */
function formatRaw(raw: string): { display: string; numeric: number | undefined } {
  const cleaned = raw.replace(/[^\d,]/g, "");
  const commaIndex = cleaned.indexOf(",");
  const hasComma = commaIndex !== -1;
  let intPart = hasComma ? cleaned.slice(0, commaIndex) : cleaned;
  const decPart = hasComma ? cleaned.slice(commaIndex + 1).replace(/,/g, "").slice(0, 2) : "";

  intPart = intPart.replace(/^0+(?=\d)/, "");

  if (intPart === "" && !hasComma) {
    return { display: "", numeric: undefined };
  }

  const intFormatted = (intPart === "" ? 0 : Number(intPart)).toLocaleString("pt-BR");
  const display = hasComma ? `${intFormatted},${decPart}` : intFormatted;
  const numeric = Number(`${intPart || "0"}.${decPart || "0"}`);

  return { display, numeric: Number.isNaN(numeric) ? undefined : numeric };
}

function numberToDisplay(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return "";
  const [intPart, decPart] = value.toFixed(2).split(".");
  const intFormatted = Number(intPart).toLocaleString("pt-BR");
  return decPart === "00" ? intFormatted : `${intFormatted},${decPart}`;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ label, error, value, onChange, className, id, name, placeholder = "0,00", disabled, autoFocus }, forwardedRef) => {
    const inputId = id ?? name;
    const [display, setDisplay] = useState(() => numberToDisplay(value));
    const lastEmitted = useRef(value);

    // sincroniza se o valor mudar por fora (ex: reset de formulário, edição carregando dados)
    useEffect(() => {
      if (value !== lastEmitted.current) {
        setDisplay(numberToDisplay(value));
        lastEmitted.current = value;
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const el = e.target;
      const cursorBefore = el.selectionStart ?? el.value.length;
      const realCharsBefore = countRealCharsBefore(el.value, cursorBefore);

      const { display: nextDisplay, numeric } = formatRaw(el.value);
      setDisplay(nextDisplay);
      lastEmitted.current = numeric;
      onChange(numeric);

      requestAnimationFrame(() => {
        const nextPos = positionAfterRealChars(nextDisplay, realCharsBefore);
        el.setSelectionRange(nextPos, nextPos);
      });
    };

    return (
      <div className={styles.wrapper}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}
        <input
          ref={forwardedRef}
          id={inputId}
          name={name}
          type="text"
          inputMode="decimal"
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          value={display}
          onChange={handleChange}
          className={cn(styles.control, error && styles.controlError, className)}
        />
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";
